/**
 * Auth redirect utilities (shared, no Next-specific imports)
 */

import { ReadonlyURLSearchParams, redirect } from "next/navigation";

/** Ensure the redirect path is a safe, absolute path within the app. */
function sanitizeRedirectPath(
  input: string | null | undefined,
  fallback: string,
): string {
  if (!input) return fallback;
  if (!input.startsWith("/")) return fallback;
  // Prevent protocol-relative URLs like //evil.com
  if (input.startsWith("//")) return fallback;
  return input;
}

/** Build a redirect URL from pathname + search components. */
export function buildRedirectUrlFromParts(
  pathname: string,
  search: string,
): string {
  return `${pathname}${search ? `?${search}` : ""}`;
}

/** Build a login URL that returns to the given safe path after auth. */
export function buildLoginRedirectUrl(path: string): string {
  const safe = sanitizeRedirectPath(path, "/");
  return `/login?redirectUrl=${encodeURIComponent(safe)}`;
}

/**
 * Get a safe redirectUrl value from the current search params.
 */
export function getRedirectUrlFromSearch(
  searchParams: ReadonlyURLSearchParams,
  defaultRedirect: string,
): string {
  const raw = searchParams.get("redirectUrl");
  return sanitizeRedirectPath(raw, defaultRedirect);
}

/** Redirect (server) to login for a given app path. */
export function redirectToLoginForPath(
  path: string,
  fallback: string = "/creator",
): never {
  const safe = sanitizeRedirectPath(path, fallback);
  redirect(buildLoginRedirectUrl(safe));
}
